import { Box, Button, Heading, Icon, Stack, Text } from '@chakra-ui/react';
import { FaArrowLeft, FaTools } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

export interface ComingSoonProps {
    /** Short title above the message (e.g. the page name). */
    title?: string;
    /** Override the default message body. */
    message?: string;
    /** Estimated availability — shown as a small badge below the message. */
    eta?: string;
}

export default function ComingSoon({
    title = 'Coming Soon',
    message = "We're putting the finishing touches on this feature. Check back soon.",
    eta,
}: ComingSoonProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const pageName = location.pathname
        .split('/')
        .filter(Boolean)
        .pop()
        ?.replace(/-/g, ' ')
        ?.replace(/\b\w/g, c => c.toUpperCase()) || '';

    return (
        <Stack
            w={'95%'}
            minH={'70vh'}
            justify={'center'}
            align={'center'}
            spacing={6}
            p={10}
            bg={'white'}
            rounded={'2xl'}
            boxShadow={'lg'}
        >
            <Box
                p={6}
                rounded={'full'}
                bgGradient={'linear(to-br, primary.100, primary.400)'}
                color={'white'}
                boxShadow={'md'}
            >
                <Icon as={FaTools} boxSize={'48px'} />
            </Box>

            <Stack spacing={2} align={'center'} maxW={'lg'} textAlign={'center'}>
                <Heading
                    size={'xl'}
                    bgGradient={'linear(to-r, primary.300, primary.600)'}
                    bgClip={'text'}
                >
                    {title}
                </Heading>
                {pageName && (
                    <Text color={'gray.500'} fontSize={'md'}>
                        {pageName}
                    </Text>
                )}
                <Text color={'gray.600'} fontSize={'lg'} mt={2}>
                    {message}
                </Text>
                {eta && (
                    <Text
                        mt={2}
                        px={3}
                        py={1}
                        bg={'orange.100'}
                        color={'orange.700'}
                        rounded={'full'}
                        fontSize={'sm'}
                        fontWeight={'semibold'}
                    >
                        Available {eta}
                    </Text>
                )}
            </Stack>

            <Stack direction={'row'} spacing={3}>
                <Button
                    leftIcon={<FaArrowLeft />}
                    variant={'outline'}
                    onClick={() => navigate(-1)}
                >
                    Go Back
                </Button>
                <Button
                    bgGradient={'linear(to-r, primary.400, primary.600)'}
                    color={'white'}
                    _hover={{ bgGradient: 'linear(to-r, primary.500, primary.700)' }}
                    onClick={() => navigate('/dashboard')}
                >
                    Back to Dashboard
                </Button>
            </Stack>
        </Stack>
    );
}
